import React from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import isEmpty from 'lodash/isEmpty'
import * as translationService from '../services/translationService'
import * as networkService from './../services/networkService'
import { getCurrentStepType } from './../services/stepService'

import { Hero, FormStepper, Button, DisplayText, TextContainer, ErrorText, PropertyBar, Option } from '../components'

import * as routeService from '../services/routeService'
import flowSelectors from '../selectors/flowSelector'
import { updateApplicationInformation, PHASE_INFORMATION_SUCCESS } from '../actions/FlowAction'
import { sendApplicationToBroker, getErrorCode } from '../services/applicationSubmitService'
import Profile from './Profile/Profile'
import { validateApplication } from './../utils/validation'

const getPhase1Items = zipCode =>
  routeService.isBrusselsZipCode(zipCode) ? ['rentIntention', 'income'] : ['residents', 'rentIntention', 'income']
const phase2Items = ['residents', 'contract', 'rentIntention', 'income', 'documents']

class OverviewView extends React.Component {
  static propTypes = {
    id: PropTypes.any,
    user: PropTypes.any,
    application: PropTypes.any,
    property: PropTypes.any,
    isRentIntentionValid: PropTypes.bool,
    residentsSummary: PropTypes.string,
    rentIntentionSummary: PropTypes.string,
    incomeSummary: PropTypes.string,
    documentsSummary: PropTypes.string,
    step: PropTypes.any,
    applicationConfirmationEmail: PropTypes.any,
    updateConfirmationEmailPreferences: PropTypes.func,
    updateApplicationInformation: PropTypes.func,
    updatePhaseInformation: PropTypes.func,
  }

  state = {
    mainError: null,
    currentItem: null,
    isSceneModalOpen: false,
    confirmationEmail: true,
    applicationConfirmationEmail: true,
    isInformationModalOpen: false,
    isDeleteInformationModalOpen: false,
    email: '',
    pdfState: {},
    items: [],
    isSubmitting: false,
    touched: false,
    isBrusselsZip: false,
  }

  modalRef = {}

  componentDidMount() {
    this.setState({
      applicationConfirmationEmail: this.props.applicationConfirmationEmail,
      email: this.props.user && this.props.user.email,
      isBrusselsZip: routeService.isBrusselsZipCode(this.props.property.zip),
    })
  }

  updateConfirmationEmailPreference = ({ target: { checked } }) => {
    this.setState({
      applicationConfirmationEmail: checked,
    })
    this.props.updateConfirmationEmailPreferences(checked)
  }

  handleAcceptDataUsages = ({ target: { checked } }) => {
    this.props.updateApplicationInformation({ acceptDataUsages: checked })
  }

  handleVisitRequestClick = event => {
    event.preventDefault()
    const { application } = this.props
    const stepType = getCurrentStepType(this.props.step)
    const items = stepType === 'VISIT_REQUEST_STEPS' ? getPhase1Items(this.props.property.zip) : phase2Items
    const allStepsValid = validateApplication(application, [...items, 'overview'], this.state.isBrusselsZip)
    let submitErrors = []
    if (!allStepsValid) {
      console.warn('OverviewView, handleVisitRequestClick', { allStepsValid })
      submitErrors = [...submitErrors, translationService.overviewStepsNotValid()]
    }
    if (application.acceptDataUsages !== true) {
      submitErrors = [...submitErrors, translationService.overviewAcceptDataUsageError()]
    }
    if (submitErrors.length) {
      this.setState({
        mainError: submitErrors.join(' - '),
        isSubmitting: false,
        touched: true,
      })
    } else {
      this.setState(
        {
          isSubmitting: true,
          touched: true,
        },
        async () => {
          try {
            const now = new Date()
            await sendApplicationToBroker(this.props.id, this.props.property.id, this.props.step)
            routeService.goToThankyouPage()
            this.props.updatePhaseInformation(now.toISOString(), this.props.step)
          } catch (err) {
            console.log(err)
            // Read the code instead of destructuring it. A network failure carries no
            // response at all, and unpacking err.response.data.error threw a TypeError there,
            // which replaced the real error with a crash.
            if (getErrorCode(err) === 'EMAIL_NOT_VERIFIED') {
              this.setState({
                touched: true,
                mainError: translationService.t('system.error.EMAIL_NOT_VERIFIED'),
                isSubmitting: false,
              })
            } else {
              this.setState({
                touched: true,
                mainError: translationService.t('system.500.server.error'),
                isSubmitting: false,
              })
            }
          }
        },
      )
    }
  }

  onChange({ target: { name, value } }) {
    this.setState({
      [name]: value,
    })
  }

  sendPdf = () => {
    const pdfSuccess = translationService.t('HISTORY_MAIL_SENDED')
    const pdfFail = translationService.t('PROFILE_ERROR_PDF_SEND')

    this.setState({ pdfState: { sending: true } })
    networkService
      .sendPdf(this.props.user.id, this.state.email)
      .then(() => {
        this.setState({ pdfState: { text: pdfSuccess } })
      })
      .catch(() => {
        this.setState({ pdfState: { text: pdfFail } })
      })
  }

  render() {
    const { mainError, touched } = this.state
    const { application, property, step } = this.props
    const stepType = getCurrentStepType(this.props.step)

    const items = stepType === 'VISIT_REQUEST_STEPS' ? getPhase1Items(property.zip) : phase2Items
    const allStepsValid = validateApplication(
      application,
      [...items, 'overview'],
      routeService.isBrusselsZipCode(property.zip),
    )

    return (
      <div>
        {!isEmpty(property) ? (
          <React.Fragment>
            {property && <PropertyBar property={property} />}
            <FormStepper steps={routeService.getStepperRoutes(step, property.zip)} />
            <Hero>
              <TextContainer>
                <DisplayText>
                  {application.contacts && (
                    <React.Fragment>
                      {allStepsValid
                        ? translationService.overviewTitle(application.contacts[0].firstname)
                        : translationService.overviewTitleInvalid(application.contacts[0].firstname)}
                    </React.Fragment>
                  )}
                </DisplayText>
              </TextContainer>
            </Hero>
          </React.Fragment>
        ) : (
          <Hero>
            <DisplayText size="small">
              {application.contacts && translationService.overviewEditProfile(application.contacts[0].firstname)}
            </DisplayText>
          </Hero>
        )}
        <TextContainer>
          <Profile items={items} />
          <Option
            type="checkbox"
            label={translationService.overviewAcceptDataUsage()}
            id="acceptDataUsages"
            name="acceptDataUsages"
            checked={application.acceptDataUsages}
            onChange={this.handleAcceptDataUsages}
            hasError={touched && !application.acceptDataUsages}
          />
        </TextContainer>
        <Hero>
          {mainError && (
            <div>
              <ErrorText>{mainError}</ErrorText>
              <br />
            </div>
          )}
          <Button size="large" primary loading={this.state.isSubmitting} onClick={this.handleVisitRequestClick}>
            {/* disabled= {!allStepsValid || application.acceptDataUsages !== true}> */}
            {translationService.overviewSendAction(step)}
          </Button>
          {stepType !== 'VISIT_REQUEST_STEPS' && (
            <div>
              <br />
              <Option
                type="checkbox"
                label={translationService.overviewCheckboxLabel()}
                id={`confirmationEmail`}
                inline
                name="confirmationEmail"
                checked={this.state.applicationConfirmationEmail}
                onChange={event => this.updateConfirmationEmailPreference(event)}
              />
            </div>
          )}
        </Hero>
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  updateConfirmationEmailPreferences: applicationConfirmationEmail =>
    dispatch(updateApplicationInformation({ applicationConfirmationEmail })),
  updateApplicationInformation: information => dispatch(updateApplicationInformation(information)),
  updatePhaseInformation: (completedAt, step) => {
    dispatch({
      type: PHASE_INFORMATION_SUCCESS,
      completedAt,
      step,
    })
  },
})
const mapStateToProps = ({ AuthReducer, FlowReducer }) => {
  const { user = {}, error } = AuthReducer
  const { step } = FlowReducer
  const { mobileNumber, id } = user
  return {
    mobileNumber,
    id,
    user,
    error,
    application: flowSelectors.application(FlowReducer),
    property: flowSelectors.property(FlowReducer),
    residentsSummary: flowSelectors.residentsSummary(FlowReducer),
    contractSummary: flowSelectors.contractSummary(FlowReducer),
    rentIntentionSummary: flowSelectors.rentIntentionSummary(FlowReducer),
    incomeSummary: flowSelectors.incomeSummary(FlowReducer),
    documentsSummary: flowSelectors.documentsSummary(FlowReducer),
    step,
    applicationConfirmationEmail: FlowReducer.application.applicationConfirmationEmail,
  }
}

export default compose(
  translate('translations'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(OverviewView)



// WEBPACK FOOTER //
// ./src/views/OverviewView.js